import React, { useState, useEffect, useRef } from 'react';
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  useLocation,
  useParams,
} from 'react-router-dom';
import './App.css';
import { Login } from './components/Login';
import { ProtectedRoute } from './components/ProtectedRoute';
import { fetchAuthSession } from 'aws-amplify/auth';
import AuthCallback from './components/AuthCallback';
import SignUp from './components/SignUp';
import SelaProjectsV2Page from './components/SelaProjectsV2Page';
import { SelaProjectOnboardingFlow } from './components/sela-v2/SelaProjectOnboardingFlow';
import { SelaProjectWorkspaceLayout } from './components/sela-v2/SelaProjectWorkspaceLayout';
import { SelaProjectOverviewPage } from './components/sela-v2/SelaProjectOverviewPage';
import { SelaProjectInsightsPage } from './components/sela-v2/SelaProjectInsightsPage';
import { SelaProjectDocumentsPage } from './components/sela-v2/SelaProjectDocumentsPage';
import { SelaProjectGateBoardPage } from './components/sela-v2/SelaProjectGateBoardPage';
import { SelaProjectGraphPage } from './components/sela-v2/SelaProjectGraphPage';
import { SelaProjectPeopleFollowUpsPage } from './components/sela-v2/SelaProjectPeopleFollowUpsPage';
import { SelaProjectChangelogPage } from './components/sela-v2/SelaProjectChangelogPage';
import {
  clearRumUserIdentity,
  recordErrorStage,
  recordFunnelStep,
  recordLatency,
  recordPageView,
  setRumUserIdentityFromIdToken,
} from './utils/rum';
import { buildOnboardingDestination, resolveV2OnboardingState } from './utils/v2Onboarding';

const SelaMapDebugPage = React.lazy(() =>
  import('./components/sela-v2/SelaMapDebugPage').then((module) => ({ default: module.SelaMapDebugPage })),
);

function LazyMapDebugPage() {
  return (
    <React.Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-[#f7fafd] text-sm font-medium text-[#647488]">
          Loading map debug page...
        </div>
      }
    >
      <SelaMapDebugPage />
    </React.Suspense>
  );
}

function LegacyV2ToRoot() {
  const location = useLocation();
  return <Navigate to={{ pathname: '/', search: location.search }} replace />;
}

function LegacyV2ProjectRedirect() {
  const { projectId } = useParams();
  const location = useLocation();
  if (!projectId) {
    return <Navigate to="/" replace />;
  }
  const prefix = `/v2/project/${projectId}`;
  const suffix = location.pathname.startsWith(prefix) ? location.pathname.slice(prefix.length) : '';
  return <Navigate to={{ pathname: `/project/${projectId}${suffix}`, search: location.search }} replace />;
}

function ProtectedApp({
  onSessionExpired,
  postLoginRedirect,
}: {
  onSessionExpired: () => void;
  postLoginRedirect: string | null;
}) {
  const location = useLocation();
  const lastTrackedRouteRef = useRef<string>('');
  const pendingOnboardingState = resolveV2OnboardingState(location.search);
  const protectedAuthRedirect =
    buildOnboardingDestination(pendingOnboardingState) || postLoginRedirect || '/';

  useEffect(() => {
    const routeKey = `${location.pathname}${location.search}`;
    if (routeKey === lastTrackedRouteRef.current) {
      return;
    }

    lastTrackedRouteRef.current = routeKey;
    recordPageView('protected_route', {
      route: location.pathname,
      query: location.search,
      is_authenticated: true,
    });
  }, [location.pathname, location.search]);

  return (
    <ProtectedRoute onSessionExpired={onSessionExpired}>
      <Routes>
        <Route path="/login" element={<Navigate to={protectedAuthRedirect} replace />} />
        <Route path="/signup" element={<Navigate to={protectedAuthRedirect} replace />} />
        <Route path="/auth/callback" element={<Navigate to={protectedAuthRedirect} replace />} />
        <Route path="/v2/project/:projectId/*" element={<LegacyV2ProjectRedirect />} />
        <Route path="/v2" element={<LegacyV2ToRoot />} />
        <Route path="/map-debug" element={<LazyMapDebugPage />} />
        <Route path="/" element={<SelaProjectsV2Page />} />
        <Route path="/project/:projectId" element={<SelaProjectWorkspaceLayout />}>
          <Route index element={<SelaProjectOverviewPage />} />
          <Route path="insights" element={<SelaProjectInsightsPage />} />
          <Route path="changelog" element={<SelaProjectChangelogPage />} />
          <Route path="documents" element={<SelaProjectDocumentsPage />} />
          <Route path="permit" element={<Navigate to="../timeline" replace />} />
          <Route path="gates" element={<SelaProjectGateBoardPage />} />
          <Route path="timeline" element={<SelaProjectGateBoardPage />} />
          <Route path="roadmap" element={<SelaProjectGateBoardPage />} />
          <Route path="people" element={<SelaProjectPeopleFollowUpsPage />} />
          <Route path="graph" element={<SelaProjectGraphPage />} />
          <Route path="facts" element={<Navigate to="../graph" replace />} />
          <Route path="evidence" element={<Navigate to="../graph" replace />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </ProtectedRoute>
  );
}

function PublicRoutes({ onLogin }: { onLogin: (user: string, redirectTo?: string) => void }) {
  const location = useLocation();
  const lastTrackedRouteRef = useRef<string>('');

  useEffect(() => {
    const routeKey = `${location.pathname}${location.search}`;
    if (routeKey !== lastTrackedRouteRef.current) {
      lastTrackedRouteRef.current = routeKey;
      recordPageView('public_route', {
        route: location.pathname,
        query: location.search,
        is_authenticated: false,
      });
    }
  }, [location.pathname, location.search]);

  return (
    <Routes>
      <Route path="/v2/project/:projectId/*" element={<LegacyV2ProjectRedirect />} />
      <Route path="/v2" element={<LegacyV2ToRoot />} />
      <Route path="/map-debug" element={<LazyMapDebugPage />} />
      <Route path="/project/:projectId/*" element={<Navigate to="/" replace />} />
      <Route path="/" element={<SelaProjectOnboardingFlow isAuthenticated={false} onExit={() => undefined} />} />
      <Route path="/login" element={<Login onLogin={onLogin} />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/auth/callback" element={<AuthCallback onAuthSuccess={onLogin} />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);
  const [postLoginRedirect, setPostLoginRedirect] = useState<string | null>(null);
  const bootstrappedRef = useRef(false);

  useEffect(() => {
    if (bootstrappedRef.current) {
      return;
    }
    bootstrappedRef.current = true;
    recordFunnelStep('app_bootstrap', 'start', {
      route: window.location.pathname,
    });
  }, []);

  useEffect(() => {
    const checkAuth = async () => {
      const startedAt = performance.now();
      recordFunnelStep('auth_session_check', 'start');
      try {
        const session = await fetchAuthSession();
        const idToken = session.tokens?.idToken?.toString() || '';
        const hasSession = Boolean(idToken || session.tokens?.accessToken);
        setIsAuthenticated(hasSession);
        if (hasSession && idToken) {
          setRumUserIdentityFromIdToken(idToken);
        } else {
          clearRumUserIdentity();
        }
        recordFunnelStep('auth_session_check', hasSession ? 'success' : 'failure', {
          has_session: hasSession,
        });
      } catch (error) {
        setIsAuthenticated(false);
        clearRumUserIdentity();
        recordFunnelStep('auth_session_check', 'failure', {
          has_session: false,
        });
        recordErrorStage('auth_session_check', error);
      } finally {
        recordLatency('auth_session_check', performance.now() - startedAt);
        recordFunnelStep('app_bootstrap', 'success', {
          auth_checked: true,
        });
        setAuthChecked(true);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = (_user: string, redirectTo?: string) => {
    setIsAuthenticated(true);
    setPostLoginRedirect(redirectTo || null);
  };

  const handleSessionExpired = () => {
    setIsAuthenticated(false);
    setPostLoginRedirect(null);
    clearRumUserIdentity();
    recordFunnelStep('session_state', 'failure', {
      reason: 'session_expired',
    });
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center px-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-lg border border-slate-200 p-6 md:p-8 text-center">
          <h1 className="text-2xl font-semibold text-slate-900 mb-2">Loading</h1>
          <p className="text-slate-600">Checking your session...</p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="relative">
        {!isAuthenticated ? (
          <PublicRoutes onLogin={handleLogin} />
        ) : (
          <ProtectedApp onSessionExpired={handleSessionExpired} postLoginRedirect={postLoginRedirect} />
        )}
      </div>
    </Router>
  );
}

export default App;
